// Every High Valyrian word is a link (docs/design/SIVUPALSTA.md "Every HV word is a link").
//
// <Hv> (components/Hv.tsx) is the one way HV text is rendered. Inside a <WordLinks> scope it splits
// its plain text into words and wraps each in a WordLink; a <Seam> child links as its form. Tapping
// one opens the word in the margin (PC: the digest docks level with its line, with the tether;
// phone: the word sheet) through the margin context (margin/context.tsx). Nothing is analysed while
// rendering: the word is resolved to its dictionary word(s) only when it is opened
// (margin/WordDigest.tsx, a lazy chunk), so a page full of links costs no form index.
//
// Never a link:
// - outside a <WordLinks> scope, and inside <NoWordLinks> (a study card before its reveal, an
//   exercise prompt or its options: a link would give the answer away);
// - inside an interactive control: a button, a link, a form field or its label, a summary, the HV
//   keyboard — a link inside a control is two targets in one. <Hv> checks its ancestors once,
//   before the first paint, and renders plain text there.
// The words carry no handlers: one listener on the document (margin/context.tsx) opens them, so a
// page with thousands of words (Kielioppi) renders about as fast as without links.
//
// Keyboard: the words are not tab stops (a page of Kielioppi would have thousands). Each line of
// linked words — the <Hv> element that holds them — is one tab stop (`data-word-line`, set by
// <Hv> before paint); in it ← / → move from word to word, Home / End to the first / last, Enter
// opens the focused word (on the line itself: its only word, else it steps into the first).
// The same one listener handles all of it (margin/context.tsx: wordLineKey).
import { Children, cloneElement, createContext, isValidElement, useContext, type ReactElement, type ReactNode } from 'react';
import { Seam, type SeamProps } from '../components/Seam';

const Scope = createContext(false);

/** Turn word links on for every <Hv> inside (lesson bodies, Kielioppi, examples, show lines…). */
export function WordLinks({ children }: { children: ReactNode }) {
  return <Scope.Provider value={true}>{children}</Scope.Provider>;
}

/** Turn them off inside (a card before its reveal, exercise prompts and answers). */
export function NoWordLinks({ children }: { children: ReactNode }) {
  return <Scope.Provider value={false}>{children}</Scope.Provider>;
}

/** Whether <Hv> links its words here. */
export function useWordLinks(): boolean {
  return useContext(Scope);
}

/** Ancestors in which a word stays plain text (interactive controls; an explicit opt-out). */
export const NO_LINK_INSIDE =
  'a[href], button, input, textarea, select, label, summary, [role="button"], [role="link"], [role="tab"], [role="option"], [role="menuitem"], [contenteditable="true"], .macron-keys, [data-no-word-links]';

/** A word link's element (what the margin's one listener opens: margin/context.tsx). */
export const WORD_LINK = '.hv-w[role="link"]';

/** A line of word links: the one tab stop for its words (an <Hv> element; see markWordLine). */
export const WORD_LINE = '[data-word-line]';

/** The visually hidden hint the lines point to (MarginProvider renders it once). */
export const WORD_LINE_HINT_ID = 'word-line-hint';

/** The word links that belong to `line` (not to a nested line, not inside a control). */
export function wordsOfLine(line: Element): HTMLElement[] {
  return [...line.querySelectorAll<HTMLElement>(WORD_LINK)].filter(
    (w) => w.parentElement?.closest(WORD_LINE) === line && !w.parentElement?.closest(NO_LINK_INSIDE),
  );
}

/**
 * Make `el` (an <Hv> element) the tab stop of its words when it holds any of its own, or undo it.
 * Plain DOM writes, no state: thousands of <Hv> on a Kielioppi page re-render nothing. The words
 * a nested <Hv> holds belong to that one.
 */
export function markWordLine(el: HTMLElement, on: boolean): void {
  const own = on && [...el.querySelectorAll(WORD_LINK)].some((w) => w.parentElement?.closest('.hv') === el && !w.parentElement?.closest(NO_LINK_INSIDE));
  if (own) {
    if (!el.hasAttribute('data-word-line')) {
      el.setAttribute('data-word-line', '');
      el.tabIndex = 0;
      el.setAttribute('aria-describedby', WORD_LINE_HINT_ID);
    }
  } else if (el.hasAttribute('data-word-line')) {
    el.removeAttribute('data-word-line');
    el.removeAttribute('tabindex');
    el.removeAttribute('aria-describedby');
  }
}

export interface WordLinkProps {
  /** The word as written (its case and macrons kept; the digest normalises it). */
  form: string;
  /** The reading, when the caller knows it (a lesson's l= attribute, a wiki-linked example word). */
  lemma?: string | undefined;
  children: ReactNode;
}

/**
 * One word as a link: a span carrying its form, focusable but not a tab stop (its line is). No
 * handlers and no hooks (a page of Kielioppi has thousands): the margin listens for clicks and keys
 * on `.hv-w[role=link]` once, for the whole document (margin/context.tsx), and opens the word there.
 * Use it inside an <Hv>, which turns its words back into plain text inside a control.
 */
export function WordLink({ form, lemma, children }: WordLinkProps) {
  return (
    <span className="hv-w" role="link" tabIndex={-1} data-word={form} data-word-lemma={lemma}>
      {children}
    </span>
  );
}

/** A word: letters and combining marks, with an inner apostrophe or hyphen ("ñuhys", "va-…"). */
const WORD = /[\p{L}\p{M}]+(?:['’-][\p{L}\p{M}]+)*/gu;

/**
 * Plain text → words as links, the spaces and punctuation between them as they are. `render`: what a
 * link holds instead of the bare word (its syllables, syllables/Syllables.tsx).
 */
export function linkText(text: string, keyBase = '', render?: (word: string) => ReactNode): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (const m of text.matchAll(WORD)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(
      <span key={`${keyBase}w${k++}`} className="hv-w" role="link" tabIndex={-1} data-word={m[0]}>
        {render ? render(m[0]) : m[0]}
      </span>,
    );
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const isSeam = (n: ReactNode): n is ReactElement<SeamProps> => isValidElement(n) && n.type === Seam;

/**
 * <Hv>'s children with their words linked: plain text is split into words; a <Seam> links as its
 * form; an element whose only child is a <Seam> (".ex-w" around a marked word) links as that form.
 * Anything else (stress syllables, a correction's markup) stays as it is.
 */
export function linkHvChildren(children: ReactNode, render?: (word: string) => ReactNode): ReactNode {
  let i = 0;
  return Children.map(children, (c) => {
    const key = `l${i++}`;
    if (typeof c === 'string') return <>{linkText(c, key, render)}</>;
    if (isSeam(c)) {
      return (
        <WordLink key={key} form={c.props.form}>
          {c}
        </WordLink>
      );
    }
    if (isValidElement<{ children?: ReactNode }>(c) && isSeam(c.props.children)) {
      const seam = c.props.children;
      return cloneElement(c, undefined, <WordLink form={seam.props.form}>{seam}</WordLink>);
    }
    return c;
  });
}
